import { useState } from 'react'
import { CANAUX, STATUTS, type Course, type DonneesOps } from '../api/types'
import { fcfa } from './Format'
import { Icone } from './Icone'
import { initiales } from '../api/operations'
import { ListeDeroulante } from './ListeDeroulante'
import { useDroits } from './Contexte'

/**
 * La carte d'une course — TRANSPOSITION de `courseCard(c, compact)`.
 *
 * `compacte` masque le trajet : c'est la forme utilisée dans la colonne
 * « À dispatcher » de la prise d'appel, où seules comptent la référence, le
 * client et l'affectation.
 */
export function CarteCourse({
  course,
  donnees,
  compacte = false,
  flash = false,
  surAssigner,
  surAvancer,
  surAnnuler,
  surDetail,
}: {
  course: Course
  donnees: DonneesOps
  compacte?: boolean
  /** Vient d'être créée : brève mise en évidence (`.flash`, comme l'original). */
  flash?: boolean
  surDetail?: (course: Course) => void
  surAssigner: (courseId: string, livreurId: string) => void
  surAvancer: (course: Course) => void
  surAnnuler: (courseId: string) => void
}) {
  const [livreurChoisi, setLivreurChoisi] = useState('')
  const droits = useDroits()
  const peutAgir = droits.peut('courses')

  const client = donnees.clients.find((c) => c.id === course.client_account_id)
  const statut = STATUTS[course.statut]
  const livreur = donnees.drivers.find((d) => d.id === course.driver_id)
  const canal = CANAUX[course.channel] ?? CANAUX.other
  const suivant = statut.suivant ? STATUTS[statut.suivant].libelle : null
  const terminee = course.statut === 'livree' || course.statut === 'annulee'
  /* Clore depuis la console est refusé par `set-course-status` quand
     l'organisation exige une photo, ou quand la course a un code de remise :
     seule l'app Livreur peut apporter ces preuves. On le dit AVANT le clic. */
  const clotureBloquee =
    statut.suivant === 'livree' && (donnees.org.require_delivery_proof || !!course.delivery_code)

  function annuler() {
    if (window.confirm(`Annuler la course ${course.ref} ?`)) surAnnuler(course.id)
  }

  /* Un livreur hors ligne n'est pas proposé ; un livreur en course l'est, mais
     annoncé comme tel — c'est au dispatch de trancher, pas à l'écran. */
  const options = donnees.drivers
    .filter((d) => d.status !== 'offline')
    .map((d) => ({
      valeur: d.id,
      libelle: d.full_name + (d.status === 'on_course' ? ' (en course)' : ''),
    }))

  return (
    <div className={`course-card${flash ? ' flash' : ''}`}>
      <div className="cc-top">
        <div>
          <div className="cc-ref">
            {course.ref} ·{' '}
            <span className="chan-chip">
              <Icone nom={canal.icone} /> {canal.libelle}
            </span>
          </div>
          <div className="cc-client">{client?.name ?? 'Client ?'}</div>
        </div>
        <span className={`badge ${statut.pastille}`}>{statut.libelle}</span>
        {client && (
          <span className={`badge ${client.type === 'pme' ? 'b-gold' : 'b-blue'} plain`}>
            {client.type === 'pme' ? 'PME' : 'Particulier'}
          </span>
        )}
        <div className="cc-price">
          <div className="p-val">{fcfa(course.price_fcfa)}</div>
          {course.surcharge_fcfa > 0 && (
            <div className="p-sur">dont +{fcfa(course.surcharge_fcfa)} hors créneau</div>
          )}
        </div>
      </div>

      {!compacte && (
        <div className="cc-route">
          <div className="rr">
            <span className="rp pk" />
            {course.pickup}
          </div>
          <div className="rline" />
          <div className="rr">
            <span className="rp dp" />
            {course.dropoff}
          </div>
        </div>
      )}

      <div className="cc-foot">
        {course.statut === 'creee' && peutAgir ? (
          <div className="cc-driver">
            <ListeDeroulante
              valeur={livreurChoisi}
              options={[{ valeur: '', libelle: 'Choisir un livreur…' }, ...options]}
              surChangement={setLivreurChoisi}
            />
            <button
              className="btn btn-green btn-sm"
              onClick={() => surAssigner(course.id, livreurChoisi)}
            >
              Assigner
            </button>
          </div>
        ) : (
          livreur && (
            <div className="cc-driver">
              <span className="avatar">{initiales(livreur.full_name)}</span> {livreur.full_name}
            </div>
          )
        )}

        <div className="cc-actions">
          {surDetail && (
            <button className="btn btn-outline btn-sm" onClick={() => surDetail(course)}>
              Détail
            </button>
          )}
          {!terminee && peutAgir && (
            <>
              {suivant && course.statut !== 'creee' && (
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => surAvancer(course)}
                  disabled={clotureBloquee}
                  title={clotureBloquee ? "Preuve exigée : la livraison se valide depuis l'app Livreur" : undefined}
                >
                  <Icone nom="arrow" /> {suivant}
                </button>
              )}
              <button
                className="btn btn-outline btn-sm"
                onClick={annuler}
                style={{ color: 'var(--err)' }}
                aria-label={`Annuler la course ${course.ref}`}
                title="Annuler la course"
              >
                <Icone nom="x" />
              </button>
            </>
          )}
        </div>
      </div>
      {clotureBloquee && !terminee && peutAgir && (
        <div className="t-muted" style={{ marginTop: 8, fontSize: 11.5 }}>
          {course.delivery_code ? 'Code de remise' : 'Photo exigée'} : la livraison se valide depuis l'app Livreur.
        </div>
      )}
    </div>
  )
}
